import React, { FC } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * Ambient aurora layer the glass surfaces refract (guide `.aurora`).
 * Three fixed blurred blobs behind all content; static, so it composites
 * once and satisfies prefers-reduced-motion by default.
 *
 * Blobs are solid tinted disks softened by blur(90px), anchored to the
 * viewport edges so their cores hang offscreen or in a corner. Below the
 * `sm` breakpoint the blobs are hidden entirely and the layer itself paints
 * a flat three-stop radial-gradient wash instead — same tokens, no blur,
 * so phones skip the expensive filter compositing.
 */
const BLOBS = [
  // Primary red: top-left corner.
  { width: 640, height: 640, offset: { left: '-180px', top: '-120px' } },
  // Soft red: right edge.
  { width: 520, height: 520, offset: { right: '-140px', top: '32%' } },
  // Neutral: below the fold.
  { width: 560, height: 560, offset: { left: '22%', bottom: '-220px' } },
] as const;

const AuroraBackground: FC = () => {
  const theme = useTheme();
  const [a, b, c] = theme.tokens.colors.aurora;

  return (
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        [theme.breakpoints.down('sm')]: {
          background: `radial-gradient(130% 42% at 88% -8%, ${a}, transparent 58%), radial-gradient(120% 38% at -14% 102%, ${c}, transparent 60%), radial-gradient(100% 30% at 50% 52%, ${b}, transparent 72%)`,
        },
      }}
    >
      {BLOBS.map((blob, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            ...blob.offset,
            width: `${blob.width}px`,
            height: `${blob.height}px`,
            borderRadius: '50%',
            filter: 'blur(90px)',
            background: [a, b, c][i],
            [theme.breakpoints.down('sm')]: {
              display: 'none',
            },
          }}
        />
      ))}
    </Box>
  );
};

export default AuroraBackground;
